import {
  createSlice,
  createAsyncThunk,
  createSelector,
  isAnyOf
} from '@reduxjs/toolkit';
import { omit, isEqual, mergeWith } from 'lodash';

import FiatValueManager from 'utils/fiatValueManager';
import handleAsyncThunkStatus from 'redux/reducerStatus/handleAsyncThunkStatus';

const SLICE_NAME = 'tokenFiatValues';
const fiatValueManager = new FiatValueManager();

type tokenFiatValuesProps = {
  tokens: {
    [key: string]: any;
  };
};

const initialState: tokenFiatValuesProps = {
  tokens: {}
};

const fetchCoinGeckoFiatValues = createAsyncThunk(
  `${SLICE_NAME}/fetchCoinGeckoFiatValues`,
  async (values: string[]) => fiatValueManager.fetchCoinGeckoPrices(values)
);

const fetchRefFinanceFiatValues = createAsyncThunk(
  `${SLICE_NAME}/fetchRefFinanceFiatValues`,
  async () => fiatValueManager.fetchRefFinancePrices()
);

const fetchTokenFiatValues = createAsyncThunk(
  `${SLICE_NAME}/fetchTokenFiatValues`,
  async (_, { dispatch }) => {
    return Promise.allSettled([
      dispatch(fetchCoinGeckoFiatValues(['near'])),
      dispatch(fetchRefFinanceFiatValues())
    ]);
  }
);

const tokenFiatValuesSlice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addMatcher(
      isAnyOf(
        fetchCoinGeckoFiatValues.fulfilled,
        fetchRefFinanceFiatValues.fulfilled
      ),
      (state, action) => {
        mergeWith(state.tokens, action.payload, (previous, fetched) =>
          fetched?.last_updated_at > previous?.last_updated_at &&
          !isEqual(
            omit(fetched, 'last_updated_at'),
            omit(previous, 'last_updated_at')
          )
            ? fetched
            : previous
        );
      }
    );
    handleAsyncThunkStatus({
      asyncThunk: fetchTokenFiatValues,
      buildStatusPath: () => [],
      builder
    });
  }
});

export default tokenFiatValuesSlice;

export const actions = {
  fetchTokenFiatValues
};

export const selectAllTokenFiatValues = (state: any) => state[SLICE_NAME];

export const selectNearTokenFiatValueUSD = createSelector(
  (state: any) => state[SLICE_NAME].tokens.near || {},
  (near) => near.usd
);

export const selectTokensFiatValueUSD = createSelector(
  selectAllTokenFiatValues,
  ({ tokens }) => tokens || {}
);
